import React, { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useValidateSmartProgram } from "../../../hooks/useTreatmentPlans";
import {
	SmartProgramValidationRefusal,
	WorkflowFinding,
} from "../../../services/treatmentPlan.service";
import WorkflowFindingsPanel from "./WorkflowFindingsPanel";

/**
 * SIMS-9278 — "does every ending each step can reach have a result?"
 *
 * A smart program has no publish step yet (portal Q-5 = C), so this control
 * runs the server's check on the SAVED plan, and publish — once it exists —
 * calls the same one. The findings use the generator's guardrail contract,
 * so the blocked state is the existing `WorkflowFindingsPanel`, unchanged:
 * one row per (step, ending), "Day N · actionId", the server's message.
 *
 * Three outcomes, kept apart on purpose: passed (success alert), blocked
 * (findings), and a request that failed for any other reason (error alert) —
 * a network failure must never read as "nothing is missing".
 */

type Outcome =
	| { kind: "passed" }
	| { kind: "blocked"; findings: WorkflowFinding[] }
	| { kind: "failed"; message: string };

const messageOf = (refusal: SmartProgramValidationRefusal): string => {
	const message = refusal?.message;
	if (Array.isArray(message)) return message.join(" ");
	return message || "The check could not be run. Please try again.";
};

const EndingResultsCheck: React.FC<{ treatmentPlanId: string }> = ({
	treatmentPlanId,
}) => {
	const validate = useValidateSmartProgram();
	const [outcome, setOutcome] = useState<Outcome | null>(null);

	// A result belongs to the plan it was run on: cleared when the plan
	// changes, and an answer that arrives for a plan no longer shown is
	// dropped (PR #1553 review). Per-call callbacks are deliberate — React
	// Query skips them once the component is gone, so nothing is set on an
	// unmounted form.
	const currentPlan = useRef(treatmentPlanId);
	useEffect(() => {
		currentPlan.current = treatmentPlanId;
		setOutcome(null);
	}, [treatmentPlanId]);

	// Set synchronously, so a second click before the re-render that
	// disables the button cannot send a second request.
	const inFlight = useRef(false);

	const run = (): void => {
		if (inFlight.current) return;
		inFlight.current = true;
		setOutcome(null);
		const ranFor = treatmentPlanId;
		const settle = (next: Outcome): void => {
			if (currentPlan.current === ranFor) setOutcome(next);
		};
		validate.mutate(treatmentPlanId, {
			onSettled: () => {
				inFlight.current = false;
			},
			onSuccess: () => settle({ kind: "passed" }),
			onError: (error: unknown) => {
				const refusal = error as SmartProgramValidationRefusal;
				if (
					refusal?.errorType === "WORKFLOW_GUARDRAILS_FAILED" &&
					Array.isArray(refusal.findings)
				) {
					settle({ kind: "blocked", findings: refusal.findings });
				} else {
					settle({ kind: "failed", message: messageOf(refusal) });
				}
			},
		});
	};

	return (
		<Grid item xs={12}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				<Box>
					<Button
						variant="outlined"
						size="small"
						onClick={run}
						disabled={validate.isPending}
						data-testid="smart-program-validate"
					>
						{validate.isPending ? "Checking…" : "Check ending results"}
					</Button>
				</Box>

				{/* Announced, so a screen-reader user hears the outcome of the press. */}
				<Box aria-live="polite" aria-atomic="true">
					{outcome?.kind === "passed" && (
						<Alert severity="success" data-testid="smart-program-validate-ok">
							Every reachable ending has a result.
						</Alert>
					)}
					{outcome?.kind === "blocked" && (
						<WorkflowFindingsPanel findings={outcome.findings} />
					)}
					{outcome?.kind === "failed" && (
						<Alert severity="error">{outcome.message}</Alert>
					)}
				</Box>
			</Box>
		</Grid>
	);
};

export default EndingResultsCheck;
